import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {Mutation} from 'react-apollo';
import {Button, Container} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import {PulseLoader} from 'react-spinners';
import Error from '../../../common/components/Error/Error';
import PopIn from '../../../common/components/PopIn';
import {
  CancelButton,
  FooterRow,
  GradientCol,
  TitleButton,
} from '../../../common/components/StyledComp';
import styled from 'styled-components';
import gql from 'graphql-tag';
import {PRODUCTS_TYPE} from '../../../common/constants';
import {buildFormToSubmit, formStateWithData, initFormState} from '../../../utils/form';
import FormInputFactory from '../../../common/components/layout/FormInputFactory';
import AmountInput from '../../../common/components/AmountInput';
import {faPencilAlt} from '@fortawesome/free-solid-svg-icons';

const CancelButtonForm = styled(CancelButton)`
  margin-top: 0;
  width: auto;
`;

const GRAPHQL_MUTATION_UPDATE_PRODUCT = gql`
  mutation updateProduct($productInput: UpdateProductInput!) {
    updateProduct(productInput: $productInput) {
      id
      type
      createdAt
      updatedAt
      price
      quantity
      description
    }
  }
`;

const formFactory = {
  id: {
    initial: 0,
    fromData: 'id',
    toSubmit: 'productInput.id',
    required: true,
    type: 'number',
  },
  type: {
    initial: PRODUCTS_TYPE.NOTIFICATION,
    fromData: 'type',
    toSubmit: 'productInput.type',
    required: true,
    type: 'string',
  },
  price: {
    initial: 0,
    fromData: 'price',
    toSubmit: 'productInput.price',
    required: true,
    type: 'number',
    modifierOnInitial: (price) => parseInt(price) / 100,
    modifierOnSubmit: (price) => parseInt(price) * 100,
  },
  quantity: {
    initial: 0,
    fromData: 'quantity',
    toSubmit: 'productInput.quantity',
    required: true,
    type: 'number',
    modifierOnInitial: (quantity) => parseInt(quantity),
    modifierOnSubmit: (quantity) => parseInt(quantity),
  },
  description: {
    initial: '',
    fromData: 'description',
    toSubmit: 'productInput.description',
    required: true,
    type: 'string',
  },
};

const initialProduct = initFormState(formFactory);

const UpdateProduct = ({product, t}) => {

  const [modalVisible, setModalVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [updatedProduct, setUpdatedProduct] = useState(initialProduct);

  useEffect(() => {
    const newStateFilled = formStateWithData(product, formFactory);
    setUpdatedProduct(newStateFilled);
  }, [product]);

  const setState = (name, value) => {
    setUpdatedProduct({
      ...updatedProduct,
      [name]: value,
    });
  };

  const onAmountChange = e => {
    setUpdatedProduct({...updatedProduct, [e.target.name]: e.target.value});
  };

  const resetUpdateProduct = () => {
    setModalVisible(false);
    setUpdatedProduct(initialProduct);
    setSubmitted(false);
  };

  const onProductUpdated = (updateProduct, e) => {
    e.preventDefault();
    setSubmitted(true);
    if (updatedProduct.price >= 0 && updatedProduct.quantity > 0) {
      const variables = buildFormToSubmit(formFactory, updatedProduct);
      updateProduct({
        variables,
      }).then(() => {
        setModalVisible(false);
        setSubmitted(false);
      }).catch(err => {
        console.error(err);
      });
    }
  };

  return (
    <Mutation mutation={GRAPHQL_MUTATION_UPDATE_PRODUCT}>
      {(updateProduct, {loading, error}) => {
        if (loading) {
          return <PulseLoader loading={loading} />;
        } else
          return (
            <>
              <Button className="btn-update" onClick={() => setModalVisible(true)}>
                <FontAwesomeIcon icon={faPencilAlt} color="#329aef" size="sm" />
              </Button>

              <PopIn visible={modalVisible} setModalVisible={setModalVisible} title={t('addProduct.title')}>
                <Container>
                  <form
                    onSubmit={e => {
                      onProductUpdated(updateProduct, e);
                    }}
                  >
                    <FormInputFactory
                      title={t('quantity')}
                      nameInput="quantity"
                      formFactory={formFactory}
                      formState={updatedProduct}
                      onChange={setState}
                      show={submitted && updatedProduct.quantity <= 0}
                    />
                    <AmountInput
                      amount={updatedProduct.price}
                      name={'price'}
                      onChange={onAmountChange}
                      show={submitted && updatedProduct.price < 0}
                    />
                    <FormInputFactory
                      nameInput="description"
                      formFactory={formFactory}
                      formState={updatedProduct}
                      onChange={setState}
                      show={submitted && updatedProduct.description === ''}
                    />
                    <FooterRow>
                      <GradientCol sm={{span: 6, offset: 3}}>
                        {error && submitted && (
                          <Error error={t('addProduct.error')}/>
                        )}
                        <div className="popin-buttons">
                          <CancelButtonForm
                            onClick={() => {
                              resetUpdateProduct();
                            }}>
                            {t('addProduct.cancel')}
                          </CancelButtonForm>
                          <TitleButton
                            type="submit"
                            disabled={loading}
                            onClick={() => {
                              setSubmitted(true);
                            }}
                          >
                            {t('updateProduct')}
                          </TitleButton>
                        </div>
                      </GradientCol>
                    </FooterRow>
                  </form>
                </Container>
              </PopIn>
            </>
          );
      }}
    </Mutation>
  );
};

UpdateProduct.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
  }).isRequired,
};

export default compose(
  withTranslation('products'),
)(UpdateProduct);
